import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { InlineBlock } from '@/components/comm/InlineBlock';
import type { TabHeadStyle } from '@/components/comm/tabs';
import Tabs from '@/components/comm/tabs';
import Tab from '@/components/comm/tabs/Tab';
import useCustomBreakpoint, { breakpointMdSmall } from '@/components/pages/about/comm/lib/custom-breakpoint.service';
import { body02, headline04 } from '@/components/pages/about/styles/typography';
import type { MainTryoutsInfoProps } from '@/components/pages/main/tryouts/MainTryoutsInfo';
import MainTryoutsInfo from '@/components/pages/main/tryouts/MainTryoutsInfo';
import { breakpointMedium } from '@/constants/breakpoints';
import { alphaHex, colors } from '@/constants/colors';

const ApplySection = () => {
  const breakpoint = useCustomBreakpoint();

  const headFontStyle = {
    fontSize: breakpoint === 'md' ? '16px' : '18px',
    lineHeight: '150%',
    fontWeight: 700,
    textColor: `${colors.white}${alphaHex(0.5)}`,
    selectedTextColor: colors.white,
  };

  const headStyle: TabHeadStyle = {
    align: 'center',
    padding: '8px 16px 9px',
    height: 'auto',
    gap: breakpoint === 'lg' ? 8 : 0,
    backgroundColor: colors.transparent,
    borderBottomThick: 3,
    borderTopThick: 0,
    borderBottomColor: colors.transparent,
    selectedBorderBottomThick: 3,
    selectedBorderColor: colors.white,
    selectedBorderThick: 0,
    selectedBorderTopThick: 0,
    width: 'fit-content',
    shouldApplyHoverEffect: true,
    ...{ ...headFontStyle },
  };

  return (
    <Container>
      <Hgroup>
        <Title>시험 접수와 응시, 자격증 발급도 <InlineBlock>온라인으로 한 번에</InlineBlock></Title>
        <SubTitle>집에서 비대면으로 시험보고, 응시 직후 빠르게 자격증을 취득하는 유일무이 실무 역량인증시험입니다.</SubTitle>
      </Hgroup>

      <TestContainer>
        <Tabs
          headStyle={headStyle}
          overrideContainerCss={css`
            display: flex;
            flex-direction: column;
            gap: 40px;

            @media (max-width: ${breakpointMedium}) {
              gap: 24px;
            }
          `}
        >
          <Tab tabTitle="전체" overrideCss={tabOverrideCss}>
            <StyledMainTryoutsInfo />
          </Tab>
          <Tab tabTitle="PCCP" overrideCss={tabOverrideCss}>
            <StyledMainTryoutsInfo classification="PCCP" />
          </Tab>
          <Tab tabTitle="PCCE" overrideCss={tabOverrideCss}>
            <StyledMainTryoutsInfo classification="PCCE" />
          </Tab>
          <Tab tabTitle="PCSQL" overrideCss={tabOverrideCss}>
            <StyledMainTryoutsInfo classification="PCSQL" />
          </Tab>
        </Tabs>
      </TestContainer>
    </Container>
  );
};

export default ApplySection;

const tabOverrideCss = css`
  @media (max-width: ${breakpointMdSmall}) {
    padding-right: 20px;
    padding-left: 20px;
  }
`;

const mainTryOutsInfoOverrideCss = {
  container: css`
    padding: 0 16px;
    column-gap: 24px;

    @media (max-width: ${breakpointMedium}) {
      padding: 0 20px;
      column-gap: 16px;
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: ${breakpointMdSmall}) {
      padding: 0;
      grid-template-columns: repeat(1, 1fr);
      row-gap: 16px;
    }
  `,
  item: css`
    border: 0 solid transparent;
    box-shadow: 0px 10px 40px 0px rgba(0, 0, 0, 0.10);

    @media (max-width: ${breakpointMedium}) {
      padding: 32px 24px;
    }
  `,
};

const StyledMainTryoutsInfo = (props: MainTryoutsInfoProps) => {
  const breakpoint = useCustomBreakpoint();

  const layouts = {
    sm: 'default',
    mdSmall: 'default',
    md: 'narrow',
    lg: 'default',
  } as const;

  return (
    <MainTryoutsInfo
      {...props}
      layout={layouts[breakpoint] || 'default'}
      overrideCss={mainTryOutsInfoOverrideCss}
    />
  );
};

const Container = styled.section`
position: relative;
display: flex;
width: 100%;
flex-direction: column;
align-items: center;
padding: 64px 0 0;
gap: 32px;

@media (max-width: ${breakpointMdSmall}) {
  padding: 60px 0;
}

&::before {
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  width: 100%;
  height: 474px;
  background: #132041;
  content: '';

  @media (max-width: ${breakpointMdSmall}) {
    height: 100%;
  }
}
`;

const Hgroup = styled.hgroup`
display: flex;
max-width: 1200px;
flex-direction: column;
align-items: center;
justify-content: center;
padding: 0px 20px;
gap: 8px;
`;

const Title = styled.h3`
  ${headline04}

  color: ${colors.white};
  text-align: center;
`;

const SubTitle = styled.p`
  ${body02}

  color: ${colors.white};
  opacity: 0.9;
  text-align: center;
`;

const TestContainer = styled.div`
width: 100%;
max-width: 1200px;
`;
